import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  Building, 
  FileText, 
  ClipboardList,
  LogOut,
  Menu,
  ChevronLeft,
  ChevronRight,
  Shield,
  Settings,
  CalendarClock,
  List,
  Zap,
  FolderOpen,
  Activity,
  CheckSquare,
  Palette,
  Trash2,
  RefreshCw,
  BarChart2,
  History,
  ChevronDown,
  ShieldCheck
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useDataCache } from '@/hooks/useDataCache';
import { ScriptInjector } from '@/components/ScriptInjector';
import { AIChatBubble } from '@/components/AIChatBubble';

export default function AppLayout() {
  const { user, logout, isLoading } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const { getCompanyData, cachedData } = useDataCache();

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Load company data on component mount and when user changes
  useEffect(() => {
    if (user) {
      getCompanyData();
    }
  }, [user, getCompanyData]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
            <img src="/qpulse-logo.png" alt="QPulseMES" className="w-12 h-12 object-contain" />
          </div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to tenant-specific login page, using saved tenant or default
    const tenantSlug = localStorage.getItem('tenantSlug') || 'demo-corp';
    return <Navigate to={`/${tenantSlug}/login`} replace />;
  }

  // Filter navigation items based on user permissions
  const allNavItems = [
    { path: '/app/dashboard', label: t('app.dashboard'), icon: LayoutDashboard, permission: null }, // Dashboard available to all
    { path: '/app/production-orders', label: t('app.productionOrders'), icon: ClipboardList, permission: 'orders:list' },
    { path: '/app/diseno', label: 'Diseño', icon: Palette, permission: 'design:upload' },
    // ROT-021: each of these three used to share orders:list with each
    // other AND with "Órdenes de Producción" (line above), so none could be
    // shown/hidden independently. Now decoupled — see roles.ts's
    // AVAILABLE_PERMISSIONS.orders for the corresponding backend entries.
    { path: '/app/planning-board', label: 'Tablero de Planeación', icon: CalendarClock, permission: 'orders:planning-board' },
    { path: '/app/planning-simulations', label: 'Simulaciones de Planeación', icon: History, permission: 'orders:planning-board' },
    { path: '/app/plant-execution', label: 'Ejecución de Planta', icon: Zap, permission: 'plant:execute' },
    { path: '/app/visor-360', label: 'Visor 360 RT', icon: Activity, permission: 'events:monitor' },
    { path: '/app/master-list', label: 'Lista Maestra', icon: List, permission: 'orders:master-list' },
    { path: '/app/indicadores', label: 'Indicadores', icon: BarChart2, permission: 'reports:view' },
    { path: '/app/production-closure', label: 'Cierre de Producción', icon: CheckSquare, permission: 'closures:manage' },
  ];

  // Administración: collapsible group at the bottom of the menu
  const adminItems = [
    { path: '/app/catalogs', label: 'Catálogos', icon: FolderOpen, permission: 'catalogs:list' },
    { path: '/app/users', label: t('app.users'), icon: Users, permission: 'users:list' },
    { path: '/app/roles', label: 'Roles y Permisos', icon: Shield, permission: 'roles:list' },
    { path: '/app/company', label: t('app.company'), icon: Building, permission: 'company:read' },
    { path: '/app/audit', label: t('app.audit'), icon: FileText, permission: 'audit:read' },
    { path: '/app/settings', label: 'Configuración', icon: Settings, permission: 'integration:update' },
    { path: '/app/comercia-sync', label: 'Sincronizar Comercia', icon: RefreshCw, permission: 'orders:import' },
    { path: '/app/orders-purge', label: 'Depuración de Órdenes', icon: Trash2, permission: 'orders:purge' },
  ];

  const navItems = allNavItems.filter(item => 
    !item.permission || (user?.permissions && user.permissions.includes(item.permission))
  );

  const visibleAdminItems = adminItems.filter(item =>
    !item.permission || (user?.permissions && user.permissions.includes(item.permission))
  );
  const adminHasActive = visibleAdminItems.some(item => location.pathname === item.path);
  const adminExpanded = adminOpen || adminHasActive;

  const renderLink = (item: (typeof allNavItems)[number], nested = false) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    return (
      <Link
        key={item.path}
        to={item.path}
        className={cn(
          "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors group relative",
          isActive
            ? "bg-blue-50 text-blue-700"
            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
          sidebarCollapsed ? "justify-center" : "",
          nested && !sidebarCollapsed && "pl-8"
        )}
        onClick={() => setSidebarOpen(false)}
        title={sidebarCollapsed ? item.label : undefined}
      >
        <Icon className={cn("h-5 w-5", !sidebarCollapsed && "mr-3")} />
        {!sidebarCollapsed && (
          <span>{item.label}</span>
        )}
        {sidebarCollapsed && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
            {item.label}
          </div>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-black/25" />
        </div>
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out",
        // Mobile behavior
        "lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        // Desktop collapse behavior
        sidebarCollapsed ? "lg:w-16" : "lg:w-64",
        // Mobile width is always full when open
        "w-64"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo and Collapse Button */}
          <div className="flex items-center justify-between px-4 py-4 border-b">
            {!sidebarCollapsed && (
              <div className="flex items-center">
                <img 
                  key={cachedData.company?.logoUrl || "default"} 
                  src={cachedData.company?.logoUrl || "/qpulse-logo.png"} 
                  alt="QPulseMES" 
                  className="h-20 w-30 object-contain rounded"
                />
                {!cachedData.company?.logoUrl && <span className="ml-3 text-xl font-bold">QPulseMES</span>}
              </div>
            )}
            {sidebarCollapsed && (
              <div className="mx-auto">
                <img 
                  key={cachedData.company?.logoUrl || "default-collapsed"} 
                  src={cachedData.company?.logoUrl || "/qpulse-logo.png"} 
                  alt="QPulseMES" 
                  className="h-20 w-30 object-contain rounded"
                />
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 ml-2"
              title={sidebarCollapsed ? "Expandir menú" : "Contraer menú"}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-2">
            {navItems.map((item) => renderLink(item))}

            {visibleAdminItems.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setAdminOpen(!adminExpanded)}
                  aria-expanded={adminExpanded}
                  className={cn(
                    "w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors group relative",
                    adminHasActive
                      ? "text-blue-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                    sidebarCollapsed ? "justify-center" : ""
                  )}
                  title={sidebarCollapsed ? 'Administración' : undefined}
                >
                  <ShieldCheck className={cn("h-5 w-5", !sidebarCollapsed && "mr-3")} />
                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1 text-left">Administración</span>
                      <ChevronDown className={cn("h-4 w-4 transition-transform", adminExpanded && "rotate-180")} />
                    </>
                  )}
                </button>
                {adminExpanded && (
                  <div className="mt-1 space-y-1">
                    {visibleAdminItems.map((item) => renderLink(item, true))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* User section */}
          <div className="border-t px-2 py-4">
            {!sidebarCollapsed ? (
              <div className="flex items-center justify-between px-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{user.fullName}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="p-2 flex-shrink-0"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-700">
                    {user.fullName?.charAt(0) || 'U'}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="p-2"
                  title="Cerrar sesión"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={cn(
        "transition-all duration-300 ease-in-out lg:pl-64",
        sidebarCollapsed && "lg:pl-16"
      )}>
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white shadow-sm border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="lg:hidden flex items-center space-x-3">
              <img 
                key={cachedData.company?.logoUrl || "default-mobile"} 
                src={cachedData.company?.logoUrl || "/qpulse-logo.png"} 
                alt="QPulseMES" 
                className="h-20 w-30 object-contain rounded"
              />
              {!cachedData.company?.logoUrl && <span className="font-bold">QPulseMES</span>}
            </div>
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-gray-900">
                {t('app.welcome')}, {user.fullName}
              </h1>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>

      {/* Script injector for custom widgets */}
      <ScriptInjector />

      {/* AI Chat Bubble */}
      <AIChatBubble />
    </div>
  );
}